import api, { getApiErrors } from '@app/services/api';
import type {
	ApiError,
	BrandAddParams,
	BrandAddResponse,
	BrandDeleteParams,
	BrandDeleteResponse,
	BrandEditParams,
	BrandEditResponse,
	BrandListParams,
} from '@app/services/api.types';
import { apiEndpoints, defaultErrorMessage } from '@app/utils/constants';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import useApp from '../useApp';
import { fn } from "@app/utils";

export const createBrand = async (params: BrandAddParams): Promise<BrandAddResponse> => {
	const url = apiEndpoints.brands.add;
	const response = await api.post(url, params);
	return response?.data?.data;
};

export const updateBrand = async (params: BrandEditParams): Promise<BrandEditResponse> => {
	const url = apiEndpoints.brands.update(params.id);
	const response = await api.put(url, params);
	return response?.data?.data;
};

export const removeBrand = async (
	params: BrandDeleteParams
): Promise<BrandDeleteResponse> => {
	const url = apiEndpoints.brands.delete(params.id);
	const response = await api.delete(url);
	return response?.data?.data;
};

export const exportBrandList = async (params: BrandListParams) => {
	const url = apiEndpoints.brands.getListExport(params);
	const response = await api.get<Blob>(url, {
		responseType: 'blob',
	});
	return response;
};

const useBrandActions = () => {
	const { message } = useApp();

	const addBrand = useMutation<BrandAddResponse, AxiosError<ApiError>, BrandAddParams>({
		mutationFn: createBrand,
		onSuccess: () => {
			message.success({
				type: 'success',
				content: 'Brand added successfully',
			});
		},
		onError: (error: AxiosError<ApiError>) => {
			const response = error.response?.data;
			const errorMessage = getApiErrors(response?.data, true);
			message.error({
				type: 'error',
				content: errorMessage || defaultErrorMessage,
			});
		},
		meta: {
			invalidates: [['brand', 'list']],
		},
	});

	const editBrand = useMutation<BrandEditResponse, AxiosError<ApiError>, BrandEditParams>({
		mutationFn: updateBrand,
		onSuccess: () => {
			message.success({
				type: 'success',
				content: 'Brand updated successfully',
			});
		},
		onError: (error: AxiosError<ApiError>) => {
			const response = error.response?.data;
			const errorMessage = getApiErrors(response?.data, true);
			message.error({
				type: 'error',
				content: errorMessage || defaultErrorMessage,
			});
		},
		meta: {
			invalidates: [['brand', 'list']],
		},
	});

	const deleteBrand = useMutation<
		BrandDeleteResponse,
		AxiosError<ApiError>,
		BrandDeleteParams
	>({
		mutationFn: removeBrand,
		onSuccess: () => {
			message.success({
				type: 'success',
				content: 'Brand deleted successfully',
			});
		},
		onError: (error: AxiosError<ApiError>) => {
			const response = error.response?.data;
			const errorMessage = getApiErrors(response?.data, true);
			message.error({
				type: 'error',
				content: errorMessage || defaultErrorMessage,
			});
		},
		meta: {
			invalidates: [['brand', 'list']],
		},
	});
	
	const exportBrands = useMutation<
			AxiosResponse<Blob>,
			AxiosError<ApiError>,
			BrandListParams
		>({
			mutationFn: exportBrandList,
			onSuccess: (res) => {
				const match = res.headers['content-disposition'].match(/filename\s*=\s*"(.+)"/i);
				const filename = match?.[1] || 'brands.csv';
				fn.downloadFileFromBlob({
					blob: res.data,
					fileName: filename,
				});
			},
			onError: (error: AxiosError<ApiError>) => {
				const response = error.response?.data;
				const errorMessage = getApiErrors(response?.data, true);
				message.error({
					type: 'error',
					content: errorMessage || defaultErrorMessage,
				});
			},
		});

	return {
		addBrand,
		editBrand,
		deleteBrand,
		exportBrands
	};
};

export default useBrandActions;
